/**
 * THE CONSULTATION — three questions, three cloths, the house's working.
 * STYLIST-2 · 6 September 2026. OWNER: web-frontend-engineer.
 *
 * The client's book leads with what the demo's cloth sheet has (STYLIST-1,
 * law 33): what the suit is for, what weather it is for, what he already has —
 * and THREE cloths under the answers with the house's reason in one sentence.
 * The engine is @pt/core's `recommend` (the demo engine's twin, over the
 * platform's own records); the words are the catalogue's; the reasons are
 * formality.json's. The whole book stands below, untouched: the answers here
 * rank the three, they never filter the shelf (LUX-R5(2), law 33(a)).
 *
 * An assumption is never printed as an answer: the heading says "for business"
 * (the standing occasion) until he has answered what for AND what weather,
 * then "for what you told us".
 */

import { useId, useState } from 'react';
import { t, tDynamic } from '@pt/copy';
import { CLIMATES, OWNS_KEYS, recommend, working, type StylistResult } from '@pt/core';
import type { ClothSummary } from '../lib/contracts.ts';
import { occasionLabel } from '../lib/optionCopy.ts';
import { SwatchCard } from './cloth.tsx';
import { FactLine, Statement } from './primitives.tsx';

export interface ConsultationProps {
  /** the offered cloths — the whole book the client may commission from */
  readonly cloths: readonly ClothSummary[];
  /** the occasions the book carries, in the server's order */
  readonly occasions: readonly string[];
  readonly commissionTypeLabel: string | null;
  readonly onOpen: (clothId: string) => void;
  /** the standing occasion before he answers — the commission's, or business */
  readonly defaultOccasion?: string;
}

const seasonWord = (season: string): string =>
  season === '' ? t('book.consult.season.any') : tDynamic(`book.filter.season.${season}`).split(' — ')[0]!.toLowerCase();

/** The pure text of the working, for the test and the render alike. */
export function workingText(
  result: StylistResult<ClothSummary>,
  answers: { occasion: string; season: string; owns: string; answered: { climate: boolean } },
): string | null {
  const w = working(result, { occasion: answers.occasion, climate: answers.season, owns: answers.owns, answered: answers.answered });
  if (!w || !result.lead) return null;
  const season = seasonWord(answers.season);
  const weather =
    w.whyTail === 'cut_for' ? t('book.consult.weather.cut_for', { season })
    : w.whyTail === 'relaxed_climate' ? t('book.consult.weather.relaxed_climate', { season })
    : w.whyTail === 'every_weight_said' ? t('book.consult.weather.every_weight_said')
    : t('book.consult.weather.every_weight_untold');
  const ownsSaid = answers.owns === '' ? '' : tDynamic(`book.consult.ownsSaid.${answers.owns}`);
  const owns =
    w.ownsTail === 'plains_step_back' ? t('book.consult.owns.not_another', { owns: ownsSaid })
    : w.ownsTail === 'relaxed_owns' ? t('book.consult.owns.relaxed_owns', { owns: ownsSaid })
    : '';
  return t('book.consult.working', {
    clothName: result.lead.name,
    occasion: occasionLabel(answers.occasion).toLowerCase(),
    reason: w.whyHead,
    weather,
    owns,
  });
}

export function Consultation({ cloths, occasions, commissionTypeLabel, onOpen, defaultOccasion = 'business' }: ConsultationProps) {
  const id = useId();
  const [occasion, setOccasion] = useState(occasions.includes(defaultOccasion) ? defaultOccasion : (occasions[0] ?? 'business'));
  const [season, setSeason] = useState('');
  const [owns, setOwns] = useState('');
  const [touched, setTouched] = useState({ occasion: false, season: false });

  const result = recommend<ClothSummary>({ cloths, garment: 'two', occasion, climate: season, owns });
  if (!result.lead) return null;
  const told = touched.occasion && touched.season;
  const heading = told
    ? t('book.consult.heading.told')
    : t('book.consult.heading.for', { occasion: occasionLabel(occasion).toLowerCase() });
  const said = workingText(result, { occasion, season, owns, answered: { climate: touched.season } });

  return (
    <section className="pt-block pt-consult" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} className="pt-record__groupHeading">{heading}</h2>
      {said === null ? null : <Statement>{said}</Statement>}
      {/* THE ORDER (6 September 2026, the STYLIST-1 judge round's "three judges,
          one order", law 33(a)): the three questions, then the three cloths
          UNDER them — the lede says the cloths follow the answers, so it stands
          above what it describes. Measured on the live book before: the cards
          came first and the questions were below the third card's facts. */}
      <FactLine>{t('book.consult.lede')}</FactLine>
      <div className="pt-consult__questions">
        <div className="pt-input">
          <label className="pt-input__label" htmlFor={`${id}-occasion`}>{t('book.consult.q.occasion')}</label>
          <select id={`${id}-occasion`} className="pt-input__field" value={occasion}
            onChange={(e) => { setOccasion(e.target.value); setTouched((x) => ({ ...x, occasion: true })); }}>
            {occasions.map((key) => <option key={key} value={key}>{occasionLabel(key)}</option>)}
          </select>
        </div>
        <div className="pt-input">
          <label className="pt-input__label" htmlFor={`${id}-season`}>{t('book.consult.q.season')}</label>
          <select id={`${id}-season`} className="pt-input__field" value={season}
            onChange={(e) => { setSeason(e.target.value); setTouched((x) => ({ ...x, season: true })); }}>
            {CLIMATES.map((key) => (
              <option key={key} value={key}>{key === '' ? t('book.consult.season.any') : tDynamic(`book.filter.season.${key}`)}</option>
            ))}
          </select>
        </div>
        <div className="pt-input">
          <label className="pt-input__label" htmlFor={`${id}-owns`}>{t('book.consult.q.owns')}</label>
          <select id={`${id}-owns`} className="pt-input__field" value={owns} onChange={(e) => setOwns(e.target.value)}>
            {OWNS_KEYS.map((key) => <option key={key} value={key}>{tDynamic(`book.consult.owns.${key === '' ? 'none' : key}`)}</option>)}
          </select>
        </div>
      </div>
      <div className="pt-grid pt-consult__three" role="list">
        {result.three.map((pick) => (
          <div key={pick.item.id} role="listitem" className="pt-consult__card">
            <p className="pt-eyebrow pt-consult__eyebrow">
              {tDynamic(`book.consult.role.${pick.role === 'held' ? 'lead' : pick.role}`)}
              {pick.offClimate ? ` · ${t('book.consult.forWeather', { season: seasonWord(pick.item.seasonClass) })}` : ''}
            </p>
            <SwatchCard cloth={pick.item} commissionTypeLabel={commissionTypeLabel} href={`/book/${pick.item.id}`} onOpen={onOpen} />
          </div>
        ))}
      </div>
      <FactLine>{t('book.consult.basis')}</FactLine>
    </section>
  );
}
